import { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Divider from "@mui/material/Divider";
import { GridActionsCellItem, type GridColDef } from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/Delete";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SecurityIcon from "@mui/icons-material/Security";
import { api } from "../lib/api";
import { useAsync } from "../lib/hooks";
import { useI18n } from "../lib/i18n";
import type { CmsFeaturePermissions, Role } from "../lib/types";
import { Button, Card, ErrorBox, Field, Loading, Modal } from "../components/ui";
import { DataTable } from "../components/DataTable";
import { newestFirst } from "../lib/format";

export function Roles() {
  const { t, fmtDate } = useI18n();
  const { data: rolesData, loading: rolesLoading, error: rolesError, reload: reloadRoles } = useAsync(
    () => api.roles.list(),
    []
  );
  const { data: permissionsData, loading: permissionsLoading } = useAsync(
    () => api.permissions.list(),
    []
  );

  const [editing, setEditing] = useState<Role | "new" | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const roles = rolesData ?? [];
  const permissions: CmsFeaturePermissions[] = useMemo(() => {
    if (Array.isArray(permissionsData)) return permissionsData;
    if (permissionsData && Array.isArray((permissionsData as any).features)) {
      return (permissionsData as any).features;
    }
    return [];
  }, [permissionsData]);

  async function remove(role: Role) {
    if (role.isSystem) {
      alert(t("roles.cannotDeleteSystem"));
      return;
    }
    if (!confirm(t("roles.confirmDelete", { name: role.name }))) return;
    setBusy(`del-${role.id}`);
    setActionError(null);
    try {
      await api.roles.delete(role.id);
      await reloadRoles();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  const columns = useMemo<GridColDef<Role>[]>(
    () => [
      {
        field: "name",
        headerName: t("roles.name"),
        flex: 1,
        minWidth: 150,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {row.name}
            </Typography>
            <Chip
              size="small"
              variant={row.isSystem ? "filled" : "outlined"}
              color={row.isSystem ? "primary" : "default"}
              label={row.isSystem ? t("roles.systemRole") : t("roles.customRole")}
              sx={{ height: 20, fontSize: 11 }}
            />
          </Stack>
        ),
      },
      {
        field: "description",
        headerName: t("roles.description"),
        flex: 1.5,
        minWidth: 200,
        renderCell: ({ row }) => (
          <Typography variant="body2" color="text.secondary" noWrap>
            {row.description || "—"}
          </Typography>
        ),
      },
      {
        field: "permissions",
        headerName: t("roles.permissions"),
        flex: 1.2,
        minWidth: 180,
        renderCell: ({ row }) => {
          const rowPerms = Array.isArray(row.permissions) ? row.permissions : [];
          const isWildcard = rowPerms.includes("*");
          return (
            <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
              {isWildcard ? (
                <Chip
                  size="small"
                  color="warning"
                  label={t("roles.featureAll")}
                  icon={<SecurityIcon fontSize="inherit" />}
                />
              ) : (
                <Chip
                  size="small"
                  variant="outlined"
                  label={t("roles.permissionsCount", { count: rowPerms.length })}
                />
              )}
            </Stack>
          );
        },
      },
      {
        field: "createdAt",
        headerName: t("roles.created"),
        flex: 0.8,
        minWidth: 130,
        renderCell: ({ row }) => (
          <Typography variant="caption" color="text.secondary">
            {fmtDate(row.createdAt)}
          </Typography>
        ),
      },
      {
        field: "actions",
        type: "actions",
        headerName: "",
        width: 100,
        getActions: ({ row }) => [
          <GridActionsCellItem
            key="edit"
            icon={<EditOutlinedIcon fontSize="small" />}
            label={t("common.edit")}
            onClick={() => setEditing(row)}
            disabled={busy !== null}
          />,
          <GridActionsCellItem
            key="del"
            icon={<DeleteIcon fontSize="small" />}
            label={t("common.delete")}
            onClick={() => remove(row)}
            disabled={row.isSystem || busy !== null}
          />,
        ],
      },
    ],
    [t, fmtDate, busy]
  );

  return (
    <Stack spacing={2.5}>
      <Card
        title={t("roles.title")}
        actions={
          <Button variant="primary" onClick={() => setEditing("new")}>
            + {t("roles.add")}
          </Button>
        }
      >
        <Typography variant="body2" color="text.secondary">
          {t("roles.subtitle")}
        </Typography>

        {actionError && <ErrorBox message={actionError} />}
        {rolesError && <ErrorBox message={rolesError} />}

        {rolesLoading && !rolesData ? (
          <Loading />
        ) : (
          <DataTable
            rows={newestFirst(roles, "createdAt")}
            columns={columns}
            loading={busy !== null || rolesLoading}
          />
        )}
      </Card>

      {editing && (
        <RoleModal
          role={editing === "new" ? null : editing}
          featurePermissions={permissions}
          loading={permissionsLoading}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await reloadRoles();
          }}
        />
      )}
    </Stack>
  );
}

function RoleModal({
  role,
  featurePermissions,
  loading = false,
  onClose,
  onSaved,
}: {
  role: Role | null;
  featurePermissions: CmsFeaturePermissions[];
  loading?: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const [name, setName] = useState(role?.name ?? "");
  const [description, setDescription] = useState(role?.description ?? "");
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>(
    Array.isArray(role?.permissions) ? role.permissions : []
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const safeFeaturePermissions = useMemo<CmsFeaturePermissions[]>(() => {
    if (Array.isArray(featurePermissions)) return featurePermissions;
    const maybeObj = featurePermissions as unknown as { features?: CmsFeaturePermissions[] };
    if (maybeObj && Array.isArray(maybeObj.features)) {
      return maybeObj.features;
    }
    return [];
  }, [featurePermissions]);

  const allCodes = useMemo(
    () =>
      safeFeaturePermissions.flatMap(f =>
        (Array.isArray(f.permissions) ? f.permissions : []).map(p => p.code)
      ),
    [safeFeaturePermissions]
  );

  const hasWildcard = selectedPermissions.includes("*");

  function togglePermission(code: string) {
    if (hasWildcard) return;
    setSelectedPermissions(prev =>
      prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]
    );
  }

  function toggleSelectAll() {
    if (hasWildcard) {
      setSelectedPermissions([]);
      return;
    }
    if (selectedPermissions.length === allCodes.length) {
      setSelectedPermissions([]);
    } else {
      setSelectedPermissions([...allCodes]);
    }
  }

  function toggleWildcard() {
    if (hasWildcard) {
      setSelectedPermissions([]);
    } else {
      setSelectedPermissions(["*"]);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (role) {
        await api.roles.update(role.id, {
          name: role.isSystem ? role.name : name.trim(),
          description: description.trim(),
          permissions: selectedPermissions,
        });
      } else {
        await api.roles.create({
          name: name.trim().toLowerCase(),
          description: description.trim(),
          permissions: selectedPermissions,
        });
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={role ? t("roles.formEdit", { name: role.name }) : t("roles.formNew")}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit} className="form">
        {error && <ErrorBox message={error} />}

        <div className="form-row">
          <Field label={t("roles.name")} hint={t("roles.nameHint")}>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              required
              disabled={role?.isSystem}
              pattern="^[a-zA-Z0-9_\-]+$"
              placeholder="e.g. monitor"
            />
          </Field>
          <Field label={t("roles.description")} hint={t("roles.descriptionHint")}>
            <input
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder={t("roles.descPlaceholder")}
            />
          </Field>
        </div>

        <Box sx={{ mt: 1 }}>
          <Stack
            direction="row"
            sx={{ justifyContent: "space-between", alignItems: "center", mb: 1.5 }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {t("roles.permissions")} ({selectedPermissions.length})
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button type="button" onClick={toggleSelectAll} disabled={busy || hasWildcard}>
                {selectedPermissions.length === allCodes.length
                  ? t("roles.unselectAll")
                  : t("roles.selectAll")}
              </Button>
            </Stack>
          </Stack>

          {/* Wildcard * option (useful for superadmin or god roles) */}
          <Box
            sx={{
              p: 1.25,
              mb: 2,
              borderRadius: 1,
              bgcolor: hasWildcard ? "warning.light" : "action.hover",
              border: 1,
              borderColor: hasWildcard ? "warning.main" : "divider",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={hasWildcard}
                  onChange={toggleWildcard}
                  color="warning"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t("roles.featureAll")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("roles.allPermsHint")}
                  </Typography>
                </Box>
              }
            />
          </Box>

          <Box
            sx={{
              maxHeight: 380,
              overflowY: "auto",
              pr: 1,
              opacity: hasWildcard ? 0.45 : 1,
              pointerEvents: hasWildcard ? "none" : "auto",
            }}
          >
            {loading && safeFeaturePermissions.length === 0 ? (
              <Box sx={{ py: 3, display: "flex", justifyContent: "center" }}>
                <Loading />
              </Box>
            ) : (
              safeFeaturePermissions.map(group => (
                <Box key={group.key} sx={{ mb: 2 }}>
                  <Typography
                    variant="overline"
                    sx={{ fontWeight: 700, color: "primary.main", letterSpacing: "0.05em" }}
                  >
                    {group.name} ({group.key})
                  </Typography>
                  <Divider sx={{ my: 0.5 }} />
                  <Stack spacing={0.5} sx={{ pl: 0.5, pt: 0.5 }}>
                    {(Array.isArray(group.permissions) ? group.permissions : []).map(p => {
                      const checked = hasWildcard || selectedPermissions.includes(p.code);
                      return (
                        <FormControlLabel
                          key={p.code}
                          control={
                            <Checkbox
                              size="small"
                              checked={checked}
                              onChange={() => togglePermission(p.code)}
                            />
                          }
                          label={
                            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {p.code}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                — {p.description || p.name}
                              </Typography>
                            </Stack>
                          }
                        />
                      );
                    })}
                  </Stack>
                </Box>
              ))
            )}
          </Box>
        </Box>

        <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end", mt: 2 }}>
          <Button type="button" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" variant="primary" loading={busy}>
            {t("common.save")}
          </Button>
        </Stack>
      </form>
    </Modal>
  );
}
